import { useMutation, useQueryClient } from '@tanstack/react-query';
import dataService from 'services/dataService';
import { useDispatch, useSelector } from 'react-redux';

import { getSmsDashboardConversationsKey } from './useConversations';
import { showResult } from '../utils';
import { getSmsDashboardMessagesKey } from './useMessages';

import type {
  ConversationType,
  IMessagesListPages,
} from 'infrastructure/interfaces';
import type { RootDispatch, RootState } from 'store';

interface Props {
  type: ConversationType;
  read: string;
  closed: string;
  lastMessageDate: string;
  searchQuery: string;
  onSuccess: () => void;
}

interface FnProps {
  conversationIds: number[];
  close: boolean;
}

const closeOpenConversationFn = async ({ conversationIds, close }: FnProps) => {
  const API_CLOSE_REOPEN_CONVERSATION = `core/conversations/close-reopen`;

  const { data, error } = await dataService.createList(
    API_CLOSE_REOPEN_CONVERSATION,
    {
      conversationIds,
      close,
    },
  );

  if (error) {
    showResult(error);
    throw new Error(error);
  }

  return data;
};

export const useCloseOpenConversation = (props: Props) => {
  const queryClient = useQueryClient();
  const { currentConversation } = useSelector(
    (state: RootState) => state.smsDashboard,
  );
  const {
    smsDashboard: { changeCurrentConversation },
  } = useDispatch<RootDispatch>();

  const { mutateAsync, isLoading, ...other } = useMutation({
    mutationFn: closeOpenConversationFn,
    onSuccess(data) {
      const messagesKey = getSmsDashboardMessagesKey({
        conversationId: currentConversation.id,
        nextCursor: undefined,
        prevCursor: undefined,
        searchQuery: '',
        searchQueryDate: '',
      });
      const conversationsKey = getSmsDashboardConversationsKey({
        type: props.type,
        read: props.read,
        closed: props.closed,
        onlyNeedFilters: true,
      });
      props.onSuccess();
      changeCurrentConversation({
        ...currentConversation,
        closed: data[0].action === 'closed',
      });

      queryClient.invalidateQueries({ queryKey: conversationsKey });
      queryClient.setQueryData<IMessagesListPages>(
        messagesKey,
        (oldMessages) => {
          if (!oldMessages) return;
          return {
            ...oldMessages,
            pages: [
              {
                ...oldMessages.pages[0],
                items: [
                  {
                    ...oldMessages.pages[0].items[0],
                    ...data[0],
                    messageId: data[0].id,
                    updatedAt: new Date(data[0].createdAt),
                    createdAt: new Date(data[0].createdAt),
                    body: data[0].body,
                  },
                  ...oldMessages.pages[0].items,
                ],
              },
              ...oldMessages.pages,
            ],
          };
        },
      );
    },
  });

  return {
    closeOpenConversation: mutateAsync,
    closeOpenConversationLoading: isLoading,
    ...other,
  };
};
